import type React from 'react';
import type { SitemapLink, SitemapPageLayout } from 'interfaces/navigation/Sitemap';
import type { Breadcrumb } from 'interfaces/navigation/Breadcrumbs';
import Breadcrumbs from 'components/Breadcrumbs';
import tagList from 'lib/mockData/Tags/tagList';
import CustomLink, { changeRoute } from 'components/Globals/Base/Link';
import AlphabetFilter from 'components/Globals/SlideFilter';
import { generateAlphabet } from 'utils/stringUtils';
import { lettersSettings } from 'components/Globals/SlideFilter/responsiveSettings';
import ErrorBoundary from 'components/ErrorBoundary';
import { UIFragments } from 'utils/log/constants/uiFragments';
import * as S from './SitemapStyles';

export interface SitemapPageProps {
  urlList: SitemapLink[];
  pageTitle?: string;
  sitemapPageLayout: SitemapPageLayout;
  breadcrumbList?: Breadcrumb[];
  subNavList?: SitemapLink[];
  alphabetFilter?: boolean;
}

const SitemapPage: React.FC<SitemapPageProps> = ({
  urlList,
  sitemapPageLayout,
  pageTitle,
  breadcrumbList,
  subNavList,
  alphabetFilter = false,
}: SitemapPageProps) => (
  <S.SitemapPageWrapper>
    <S.Title
      as='h1'
      $fontFamily='graphikCond'
      $size={{ default: 'large', lgMin: 'xxlarge', mdMin: 'xlarge' }}
    >
      <S.SitemapStyledLink
        href='/sitemap/'
        className='sitemap-link'
        ariaLabel='Go to main sitemap page'
      >
        Site Map
      </S.SitemapStyledLink>
    </S.Title>
    {subNavList && subNavList.length > 0 && (
      <ul>
        {subNavList.map(({ uri, title }) => (
          <li key={`${title}-${uri}`}>
            <S.SitemapStyledLink
              href={uri}
              className='sitemap-link'
              ariaLabel={`Go to ${title} sitemap page`}
            >
              <h2>{title}</h2>
            </S.SitemapStyledLink>
          </li>
        ))}
      </ul>
    )}

    {breadcrumbList && breadcrumbList.length > 0 && (
      <S.SitemapBreadcrumbsWrapper>
        <Breadcrumbs items={breadcrumbList} />
      </S.SitemapBreadcrumbsWrapper>
    )}
    {pageTitle && <h2 className='sitemap-page-title'>{pageTitle}</h2>}
    {alphabetFilter && (
      <ErrorBoundary fragment={UIFragments.SITEMAP_SECTION}>
        <S.LetterPickerWrapper>
          <AlphabetFilter
            valueList={generateAlphabet()}
            selectedValue={urlList[0].title.split('')[0].toUpperCase()}
            changeSelectedValue={(letter) => {
              // eslint-disable-next-line no-void
              void changeRoute(`/sitemap/${sitemapPageLayout}/${letter}`);
            }}
            responsiveSettings={lettersSettings}
          />
        </S.LetterPickerWrapper>
      </ErrorBoundary>
    )}
    <ErrorBoundary fragment={UIFragments.SITEMAP_SECTION}>
      <ol className={`sitemap-grid-${sitemapPageLayout}`}>
        {urlList.map(({ uri, title }) => (
          <li key={`${title}-${uri}`}>
            <S.SitemapStyledLink
              href={uri}
              className='sitemap-link'
              ariaLabel={`Go to ${title} sitemap page`}
            >
              {title}
            </S.SitemapStyledLink>
          </li>
        ))}
      </ol>
    </ErrorBoundary>
    <ErrorBoundary fragment={UIFragments.SITEMAP_SECTION}>
      <S.TagList>
        {tagList?.map(({ title, uri }, index) => (
          <S.Tag
            // eslint-disable-next-line react/no-array-index-key
            key={`${title}-${uri}-${index}`}
            className='tag'
          >
            <CustomLink
              className='link'
              href={uri}
            >
              {title}
            </CustomLink>
          </S.Tag>
        ))}
      </S.TagList>
    </ErrorBoundary>
  </S.SitemapPageWrapper>
);

export { SitemapPage };
